import { useContext, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ToastContext, useFetch } from '@reveni-io/reveni-ui'

import { statuses } from '@/returns/constants'
import returnsServices from '@/returns/services/services'
import routes from '@/Router/routes.constants'

import {
  shouldShowIssueExchangeAndRefundConfirmationModal,
  shouldShowIssueExchangeConfirmationModal,
  shouldShowIssueRefundConfirmationModal,
} from './helpers'

const useChangeStatus = ({ onChangeSuccess }) => {
  const [orderId, setOrderId] = useState(null)
  const { fetchData: changeStatus } = useFetch(returnsServices.changeStatus, { doInitialRequest: false })
  const [showIssueRefundConfirmationModal, setShowIssueRefundConfirmationModal] = useState(false)
  const [showIssueExchangeConfirmationModal, setShowIssueExchangeConfirmationModal] = useState(false)
  const [showIssueExchangeAndRefundConfirmationModal, setShowIssueExchangeAndRefundConfirmationModal] = useState(false)
  const [showCancelOrderConfirmationModal, setShowCancelOrderConfirmationModal] = useState(false)
  const navigation = useNavigate()
  const { setToastInfo } = useContext(ToastContext)
  const location = useLocation()

  const performStatusChange = async params => {
    try {
      const response = await changeStatus(params)

      onChangeSuccess?.(response)
    } catch (err) {
      setToastInfo({ show: true, message: err.errorMessage, type: 'error' })
    }
  }

  const onChangeStatus = async ({
    orderId,
    status,
    issueRefundAtStatus,
    allowsIssueRefund,
    issueExchangeAtStatus,
    allowsExchangeOrderProcessing,
  }) => {
    setOrderId(orderId)

    if (status === statuses.rejected) {
      navigation(routes.transactionValidationPage.replace(':id', orderId), {
        state: { from: location, rejectAll: true },
      })
    } else if (status === statuses.accepted) {
      navigation(routes.transactionValidationPage.replace(':id', orderId), {
        state: { from: location, acceptAll: true },
      })
    } else if (status === statuses.forReview) {
      navigation(routes.transactionValidationPage.replace(':id', orderId), {
        state: { from: location, forReviewAll: true },
      })
    } else if (
      shouldShowIssueRefundConfirmationModal(
        status,
        issueRefundAtStatus,
        allowsIssueRefund,
        allowsExchangeOrderProcessing
      )
    ) {
      setShowIssueRefundConfirmationModal(true)
    } else if (
      shouldShowIssueExchangeConfirmationModal(
        status,
        issueExchangeAtStatus,
        allowsExchangeOrderProcessing,
        allowsIssueRefund
      )
    ) {
      setShowIssueExchangeConfirmationModal(true)
    } else if (
      shouldShowIssueExchangeAndRefundConfirmationModal({
        status,
        issueExchangeAtStatus,
        allowsExchangeOrderProcessing,
        issueRefundAtStatus,
        allowsIssueRefund,
      })
    ) {
      setShowIssueExchangeAndRefundConfirmationModal(true)
    } else if (status === statuses.cancelled) {
      setShowCancelOrderConfirmationModal(true)
    } else {
      await performStatusChange({ orderId, status })
    }
  }

  const handleConfirmIssueRefundOrExchangeWhenChangeStatusToReceived = async () => {
    setShowIssueRefundConfirmationModal(false)
    setShowIssueExchangeConfirmationModal(false)
    await performStatusChange({ orderId, status: statuses.received })
  }

  const handleConfirmCancelOrder = async () => {
    setShowCancelOrderConfirmationModal(false)
    await performStatusChange({ orderId, status: statuses.cancelled })
  }

  return {
    onChangeStatus,
    showIssueRefundConfirmationModal,
    setShowIssueRefundConfirmationModal,
    showIssueExchangeConfirmationModal,
    setShowIssueExchangeConfirmationModal,
    showIssueExchangeAndRefundConfirmationModal,
    setShowIssueExchangeAndRefundConfirmationModal,
    showCancelOrderConfirmationModal,
    setShowCancelOrderConfirmationModal,
    handleConfirmIssueRefundOrExchangeWhenChangeStatusToReceived,
    handleConfirmCancelOrder,
  }
}

export default useChangeStatus
